import { useCallback, useMemo, useState } from "react";
import { useQuery, useMutation, useQueryClient } from "react-query";
import { Badge, Button, Flex, Text } from "@radix-ui/themes";
import { PlusIcon } from "@radix-ui/react-icons";
import { Loader2, Play, Plus } from "lucide-react";
import { toast } from "sonner";
import { useNavigate } from "react-router-dom";

import { useRegressionApi } from "@/v2/entities/regression/Regression.api";
import { useReleaseConversationApi } from "@/v2/entities/release-conversation/ReleaseConversation.api";
import { useSimulator } from "@/v2/features/simulator/context/SimulatorWrapper";
import { Loader } from "@/components/ui/loader";
import { Navbar } from "@/components/shared/navbar/Navbar";
import { PageHeader } from "@/v2/components/layout/page-header/PageHeader";
import { StructureCheck } from "@/v2/assets/icons/StructureCheck";
import { DropdownMenu } from "@/components/shared/dropdown-menu";
import {
  Tabs as DataTabs,
  TabsList as DataTabsList,
  TabsTrigger as DataTabsTrigger,
} from "@/v2/components/dataTabs/tabs";

import { getChannelIconWithContainer } from "./channel-icon";
import { SetsView } from "./sets-view";
import { RunsView } from "./runs-view";
import { SetItemsView } from "./set-items-view";
import { QuestionsView } from "./questions-view";
import { ResultDetailView } from "./result-detail-view";
import { ItemDetailView } from "./item-detail-view";
import { AddItemDialog } from "./add-item-dialog";
import { CreateTestSetDialog } from "./create-test-set-dialog";
import { CreateEmptySetDialog } from "./create-empty-set-dialog";
import styles from "./styles.module.scss";

type NavigationLevel = "sets" | "runs";

export default function RegressionTests() {
  const { simulator, currentRelease } = useSimulator();
  const queryClient = useQueryClient();
  const regressionApi = useRegressionApi();
  const releaseConversationApi = useReleaseConversationApi();
  const navigate = useNavigate();

  const [navigationLevel, setNavigationLevel] =
    useState<NavigationLevel>("sets");
  const [selectedSetId, setSelectedSetId] = useState<string | null>(null);
  const [selectedTestId, setSelectedTestId] = useState<string | null>(null);
  const [selectedResultId, setSelectedResultId] = useState<string | null>(null);
  const [isCreateDialogOpen, setIsCreateDialogOpen] = useState(false);
  const [isCreateEmptyDialogOpen, setIsCreateEmptyDialogOpen] = useState(false);
  const [isAddItemDialogOpen, setIsAddItemDialogOpen] = useState(false);
  const [activeTab, setActiveTab] = useState<"tests" | "items">("tests");
  const [selectedItemId, setSelectedItemId] = useState<string | null>(null);


  const simulatorId = simulator?.id?.toString();

  const { data: regressionSets, isLoading: isSetsLoading } = useQuery(
    ["regression-sets", simulatorId],
    () => regressionApi.getRegressionSetsBySimulatorId(simulatorId!),
    { enabled: !!simulatorId }
  );

  const { data: testRuns, isLoading: isTestRunsLoading } = useQuery(
    ["regression-test-runs", selectedSetId],
    () => regressionApi.getRegressionTestsByRegressionSetId(selectedSetId!),
    { enabled: !!selectedSetId }
  );

  const { data: setDetail, isLoading: isSetDetailLoading } = useQuery(
    ["regression-set-detail", selectedSetId],
    () => regressionApi.getRegressionSet(selectedSetId!),
    { enabled: !!selectedSetId }
  );

  const { data: testResults, isLoading: isResultsLoading } = useQuery(
    ["regression-test-results", selectedTestId],
    () => regressionApi.getRegressionTestResults(selectedTestId!),
    { enabled: !!selectedTestId }
  );

  const runTestMutation = useMutation(
    (regressionSetId: string) =>
      regressionApi.runRegressionTestBySet({
        regressionSetId,
        releaseId: currentRelease!.id,
      }),
    {
      onSuccess: () => {
        queryClient.invalidateQueries(["regression-test-runs", selectedSetId]);
      },
    }
  );

  const forgetMutation = useMutation(
    async ({
      releaseConversationId,
      regressionSetItemId,
    }: {
      releaseConversationId: number;
      regressionSetItemId: string;
    }) => {
      await releaseConversationApi.updateReleaseConversationFeedback(
        releaseConversationId,
        { feedback: "FORGOTTEN" }
      );
      await regressionApi.deleteRegressionSetItem(regressionSetItemId);
    },
    {
      onSuccess: () => {
        queryClient.invalidateQueries([
          "regression-test-results",
          selectedTestId,
        ]);
        queryClient.invalidateQueries(["regression-sets", simulatorId]);
        setSelectedResultId(null);
      },
    }
  );

  const deleteItemMutation = useMutation(
    (itemId: string) => regressionApi.deleteRegressionSetItem(itemId),
    {
      onSuccess: () => {
        queryClient.invalidateQueries(["regression-set-detail", selectedSetId]);
        queryClient.invalidateQueries(["regression-sets", simulatorId]);
        toast.success("Item removed");
      },
      onError: () => {
        toast.error("Failed to remove item");
      },
    }
  );

  const selectedSet = useMemo(() => {
    return regressionSets?.find((set) => set.id === selectedSetId);
  }, [regressionSets, selectedSetId]);

  const selectedRun = useMemo(() => {
    return testRuns?.find((run) => run.id === selectedTestId);
  }, [testRuns, selectedTestId]);

  const selectedResult = useMemo(() => {
    return testResults?.find((result) => result.id === selectedResultId);
  }, [testResults, selectedResultId]);

  const selectedItem = useMemo(() => {
    return setDetail?.items?.find((item) => item.id === selectedItemId);
  }, [setDetail, selectedItemId]);

  const handleSelectSet = useCallback((setId: string) => {
    setSelectedSetId(setId);
    setSelectedTestId(null);
    setSelectedResultId(null);
    setSelectedItemId(null);
    setActiveTab("tests");
    setNavigationLevel("runs");
  }, []);

  const handleSelectRun = useCallback((runId: string) => {
    setSelectedTestId(runId);
    setSelectedResultId(null);
    setSelectedItemId(null);
  }, []);

  const handleSelectResult = useCallback((resultId: string) => {
    setSelectedResultId(resultId);
  }, []);

  const handleSelectItem = useCallback((itemId: string) => {
    setSelectedItemId(itemId);
    setSelectedTestId(null);
    setSelectedResultId(null);
  }, []);

  const handleBackToSets = useCallback(() => {
    setSelectedSetId(null);
    setSelectedTestId(null);
    setSelectedResultId(null);
    setSelectedItemId(null);
    setNavigationLevel("sets");
  }, []);

  const handleRunTest = useCallback(() => {
    if (selectedSetId && currentRelease) {
      runTestMutation.mutate(selectedSetId);
    }
  }, [selectedSetId, currentRelease, runTestMutation]);

  const handleForget = useCallback(() => {
    const releaseConversationId =
      selectedResult?.regressionSetItem?.releaseConversation?.id;
    const regressionSetItemId = selectedResult?.regressionSetItemId;

    if (releaseConversationId && regressionSetItemId) {
      forgetMutation.mutate({
        releaseConversationId: parseInt(releaseConversationId, 10),
        regressionSetItemId,
      });
    }
  }, [selectedResult, forgetMutation]);

  const handleForgetItem = useCallback(() => {
    if (selectedItem?.releaseConversationId && selectedItem?.id) {
      forgetMutation.mutate(
        {
          releaseConversationId: selectedItem.releaseConversationId,
          regressionSetItemId: selectedItem.id,
        },
        {
          onSuccess: () => {
            setSelectedItemId(null);
            queryClient.invalidateQueries([
              "regression-set-detail",
              selectedSetId,
            ]);
          },
        }
      );
    }
  }, [selectedItem, forgetMutation, queryClient, selectedSetId]);

  const handleRemoveItemFromSet = useCallback(() => {
    if (selectedItem?.id) {
      deleteItemMutation.mutate(selectedItem.id, {
        onSuccess: () => {
          setSelectedItemId(null);
        },
      });
    }
  }, [selectedItem, deleteItemMutation]);

  const handleRemoveResultItemFromSet = useCallback(() => {
    if (selectedResult?.regressionSetItemId) {
      deleteItemMutation.mutate(selectedResult.regressionSetItemId, {
        onSuccess: () => {
          setSelectedResultId(null);
        },
      });
    }
  }, [selectedResult, deleteItemMutation]);

  const handleCreateSuccess = useCallback(() => {
    setIsCreateDialogOpen(false);
    queryClient.invalidateQueries(["regression-sets", simulatorId]);
  }, [queryClient, simulatorId]);

  const handleAddItemSuccess = useCallback(() => {
    setIsAddItemDialogOpen(false);
    queryClient.invalidateQueries(["regression-set-detail", selectedSetId]);
    queryClient.invalidateQueries(["regression-sets", simulatorId]);
  }, [queryClient, selectedSetId, simulatorId]);

  const handleCreateEmptySuccess = useCallback(() => {
    setIsCreateEmptyDialogOpen(false);
    queryClient.invalidateQueries(["regression-sets", simulatorId]);
  }, [queryClient, simulatorId]);

  const handleNavigateToSimulator = useCallback(() => {
    if (simulator?.id) {
      navigate(`/automation/${simulator.id}`);
    }
  }, [navigate, simulator?.id]);

  const handleTabChange = useCallback((value: string) => {
    setActiveTab(value as "tests" | "items");
    setSelectedResultId(null);
  }, []);

  const breadcrumbItems = useMemo(() => {
    const items = [
      {
        title: simulator?.name || "AI Agent",
        svg: simulator
          ? getChannelIconWithContainer(simulator.channel)
          : undefined,
        onClick: handleNavigateToSimulator,
      },
      {
        title: "Regression Tests",
        onClick: navigationLevel !== "sets" ? handleBackToSets : undefined,
      },
    ];

    if (selectedSet && navigationLevel !== "sets") {
      items.push({
        title: selectedSet.name,
        onClick: undefined,
      });
    }

    return items;
  }, [
    simulator,
    navigationLevel,
    selectedSet,
    handleNavigateToSimulator,
    handleBackToSets,
  ]);

  const newMenuItems = useMemo(
    () => [
      {
        label: "Create from scratch",
        onClick: () => setIsCreateEmptyDialogOpen(true),
      },
      {
        label: "From positive feedback",
        onClick: () => setIsCreateDialogOpen(true),
      },
    ],
    []
  );

  const pageHeaderActions = useMemo(() => {
    if (navigationLevel === "sets") {
      return (
        <DropdownMenu items={newMenuItems}>
          <DropdownMenu.Trigger>
            <Button size="2" color="gray" highContrast>
              <PlusIcon />
              New
            </Button>
          </DropdownMenu.Trigger>
        </DropdownMenu>
      );
    }

    if (navigationLevel === "runs" && selectedSet) {
      return (
        <Flex gap="2" align="center">
          <Button
            size="2"
            variant="soft"
            color="gray"
            onClick={() => setIsAddItemDialogOpen(true)}
          >
            <Plus size={14} />
            Add Item
          </Button>
          <Button
            size="2"
            color="gray"
            highContrast
            onClick={handleRunTest}
            disabled={runTestMutation.isLoading}
          >
            {runTestMutation.isLoading ? (
              <>
                <Loader2 className="animate-spin" size={14} />
                Running...
              </>
            ) : (
              <>
                <Play size={14} />
                Run Test
              </>
            )}
          </Button>
        </Flex>
      );
    }

    return undefined;
  }, [
    navigationLevel,
    selectedSet,
    handleRunTest,
    runTestMutation.isLoading,
    newMenuItems,
  ]);

  const testsCount = useMemo(() => testRuns?.length ?? 0, [testRuns?.length]);

  const setItemsCount = useMemo(
    () => setDetail?.items?.length ?? selectedSet?.numberOfConversations ?? 0,
    [setDetail?.items?.length, selectedSet?.numberOfConversations]
  );

  if (!simulator) {
    return <Loader />;
  }

  return (
    <div className={styles.fullScreenContainer}>
      <div className={styles.breadcrumbBar}>
        <Navbar items={breadcrumbItems} />
      </div>

      <div className={styles.content}>
        <div className={styles.navigationPanel}>
          <PageHeader
            icon={<StructureCheck size={32} fill="gray" />}
            title={
              navigationLevel === "sets"
                ? "Regression Tests"
                : selectedSet?.name ?? "Regression Tests"
            }
            description={
              navigationLevel === "sets"
                ? `Manage and view regression tests for "${simulator.name}" agent`
                : selectedSet?.description || undefined
            }
            className={styles.pageHeader}
            actions={pageHeaderActions}
          />

          {navigationLevel === "sets" && (
            <SetsView
              regressionSets={regressionSets}
              isLoading={isSetsLoading}
              onSelectSet={handleSelectSet}
            />
          )}

          {navigationLevel === "runs" && selectedSet && (
            <>
              <div className={styles.tabsContainer}>
                <DataTabs value={activeTab} onValueChange={handleTabChange}>
                  <DataTabsList>
                    <DataTabsTrigger value="tests">
                      Tests
                      <Badge color="gray" variant="soft" size="1">
                        {testsCount}
                      </Badge>
                    </DataTabsTrigger>
                    <DataTabsTrigger value="items">
                      Set Items
                      <Badge color="gray" variant="soft" size="1">
                        {setItemsCount}
                      </Badge>
                    </DataTabsTrigger>
                  </DataTabsList>
                </DataTabs>
              </div>

              {activeTab === "tests" && (
                <RunsView
                  testRuns={testRuns}
                  isLoading={isTestRunsLoading}
                  selectedRunId={selectedTestId}
                  onSelectRun={handleSelectRun}
                />
              )}

              {activeTab === "items" && (
                <SetItemsView
                  items={setDetail?.items}
                  isLoading={isSetDetailLoading}
                  selectedItemId={selectedItemId}
                  onSelectItem={handleSelectItem}
                />
              )}
            </>
          )}
        </div>

        {selectedTestId && (
          <div className={styles.resultsPanel}>
            <QuestionsView
              testResults={testResults}
              isLoading={isResultsLoading}
              selectedResultId={selectedResultId}
              onSelectResult={handleSelectResult}
            />
          </div>
        )}

        {selectedItem && !selectedTestId && (
          <div className={styles.resultsPanel}>
            <ItemDetailView
              item={selectedItem}
              onForget={handleForgetItem}
              isForgetLoading={forgetMutation.isLoading}
              onRemoveFromSet={handleRemoveItemFromSet}
              isRemovingFromSet={deleteItemMutation.isLoading}
            />
          </div>
        )}

        {selectedTestId && (
          <div className={styles.detailPanel}>
            {selectedResult ? (
              <ResultDetailView
                result={selectedResult}
                onForget={handleForget}
                isForgetLoading={forgetMutation.isLoading}
                onRemoveFromSet={handleRemoveResultItemFromSet}
                isRemovingFromSet={deleteItemMutation.isLoading}
              />
            ) : (
              <div className={styles.emptyState}>
                <Text size="3" color="gray">
                  Select a conversation to view details
                </Text>
              </div>
            )}
          </div>
        )}

        {!selectedTestId && !selectedItem && (
          <div className={styles.detailPanel}>
            <div className={styles.emptyState}>
              <Text size="3" color="gray">
                Select a test or item to view details
              </Text>
            </div>
          </div>
        )}
      </div>

      <CreateEmptySetDialog
        open={isCreateEmptyDialogOpen}
        onOpenChange={setIsCreateEmptyDialogOpen}
        onSuccess={handleCreateEmptySuccess}
        simulatorId={simulator?.id}
      />

      <CreateTestSetDialog
        open={isCreateDialogOpen}
        onOpenChange={setIsCreateDialogOpen}
        onSuccess={handleCreateSuccess}
      />

      {selectedSetId && (
        <AddItemDialog
          open={isAddItemDialogOpen}
          onOpenChange={setIsAddItemDialogOpen}
          regressionSetId={selectedSetId}
          onSuccess={handleAddItemSuccess}
        />
      )}
    </div>
  );
}
